import React from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import { Grid, FormLabel, FormControl, TextField, Typography, Paper, Stack, RadioGroup, FormControlLabel, Radio, Divider } from '@mui/material';
import { DiagnosticType } from '../../../../Enum/DiagnosticType';
import { MiniMentalExamItem } from '../../../../Models/MiniMentalExams/MiniMentalExamItem.model';
import { copyObject } from '../../../../utils/common';
import { YesNoEnum } from '../../../../Enum/YesNoEnum';
import { setSuicidalHomicidalIdeations } from '../../../../store/assessment.slice';
import { isAIIcon } from '../../Assessment';

const HomicidalIdeation: React.FC<{ memberId: string; setIsChanged: React.Dispatch<React.SetStateAction<boolean>> }> = ({ memberId, setIsChanged }) => {
    const dispatch = useAppDispatch();
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const { suicidalHomicidalIdeations } = useSelector((state: RootState) => state.assessment);
    const isBHDiagnostic = engagementDraft?.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;

    const HOMICIDE_ASSAULT_RISK = 'Homicide/Assault risk';
    const HISTORY_OF_RUNAWAY = 'History of runaway';
    const HOMICIDAL = 'Homicidal';
    const SPECIFIC_VICTIM = 'Specific victim';
    const HIGH = 'High';
    const MODERATE = 'Moderate';

    const dtoQuestionsByType = [
        {
            type: 'Homicide',
            name: 'Homicide Concerns/Harm to Others',
            list: [
                { name: 'Homicidal', label: 'Homicidal', isMultiSelect: false },
                { name: 'PlanAdequacy', label: 'Plan adequacy', isMultiSelect: false },
                { name: 'Intentionality', label: 'Intentionality', isMultiSelect: false },
                { name: 'LethalityOfPlan', label: 'Lethality of plan', isMultiSelect: false },
                { name: 'HistoryOfAssault', label: 'History of assault', isMultiSelect: false },
                { name: 'HistoryOfFireSetting', label: 'History of fire setting', isMultiSelect: false },
                { name: 'HistoryOfGangInvolvement', label: 'History of gang involvement/group influence', isMultiSelect: false },
                { name: 'HistoryOfSexualAggression', label: 'History of sexual aggression', isMultiSelect: false },
                { name: 'HistoryOfHarmToProperty', label: 'History of harm to property', isMultiSelect: false },
            ],
        },
        {
            type: 'HomicideAssaultRisk',
            name: 'Homicide/Assault Risk Assessment',
            list: [
                { name: 'AssaultRisk', label: 'Homicide/Assault risk', isMultiSelect: false },
                { name: 'HistoryOfRunaway', label: 'History of runaway', isMultiSelect: false },
            ],
        },
    ];

    const handleOnChangeSIHIItem = (value: any, typePropsName: string, parentPropsName: string, childPropsName: string) => {
        let data = copyObject(suicidalHomicidalIdeations);
        data[typePropsName][parentPropsName][childPropsName] = value;
        if (data[typePropsName][parentPropsName]) {
            data[typePropsName][parentPropsName].IsAIAnswer = false;
        }
        dispatch(setSuicidalHomicidalIdeations(data));
        setIsChanged(true);
    };

    return (
        <Grid size={12}>
            <Typography fontStyle={'italic'} color={'error.main'}>
                If past and current concerns reported, complete homicide protocol/Homicide risk assessment:
            </Typography>
            <Paper sx={{ p: 2, mt: 2, width: '80%' }} elevation={2}>
                <Stack spacing={2}>
                    {dtoQuestionsByType.map((type) => {
                        const list = type.list;
                        return (
                            <div key={`dto-item-${type.type}`}>
                                <Typography variant="body1" component="label" fontWeight={'bold'}>
                                    {type.name}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                {list.map((item, itemIndex) => {
                                    const dtoItem = suicidalHomicidalIdeations?.[type.type]?.[item.name] as MiniMentalExamItem;
                                    if (!dtoItem) return null;
                                    return (
                                        <Stack spacing={1} mb={2} key={`dto-item-${itemIndex}`}>
                                            <FormControl fullWidth size="small" key={`dto-item-${itemIndex}`}>
                                                <FormLabel required={isBHDiagnostic} id={`${item.name}-type-select-label`}>
                                                    {item.label} {dtoItem?.IsAIAnswer && isAIIcon()}
                                                </FormLabel>
                                                <RadioGroup
                                                    row
                                                    aria-labelledby={`${item.name}-type-select-label`}
                                                    name={`${item.name}-type-select-label`}
                                                    value={Boolean(dtoItem?.Type?.Id) ? dtoItem.Type.Id : ''}
                                                    onChange={(e) => {
                                                        handleOnChangeSIHIItem(
                                                            dtoItem.TypeList.find((t) => t.Id === e.target.value),
                                                            type.type,
                                                            item.name,
                                                            'Type'
                                                        );
                                                    }}
                                                >
                                                    {dtoItem?.TypeList?.map((type) => (
                                                        <FormControlLabel key={type.Id} value={type.Id} control={<Radio />} label={type.Name} />
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                            {dtoItem.Name === HOMICIDAL && dtoItem.Type?.Name === SPECIFIC_VICTIM && (
                                                <TextField
                                                    label="Intended victim and contact information"
                                                    fullWidth
                                                    variant="outlined"
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={Boolean(dtoItem?.Description) ? dtoItem?.Description : ''}
                                                    onChange={(e) => {
                                                        handleOnChangeSIHIItem(e.target.value, type.type, item.name, 'Description');
                                                    }}
                                                />
                                            )}
                                            {dtoItem.Name === HOMICIDE_ASSAULT_RISK && (dtoItem.Type?.Name === MODERATE || dtoItem.Type?.Name === HIGH) && (
                                                <TextField
                                                    label="Details if Moderate or High"
                                                    fullWidth
                                                    variant="outlined"
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    sx={{ '.MuiFormHelperText-root': { color: 'error.main' } }}
                                                    value={Boolean(dtoItem?.Description) ? dtoItem?.Description : ''}
                                                    helperText={'REVIEW WITH SUPERVISOR IMMEDIATELY'}
                                                    onChange={(e) => {
                                                        handleOnChangeSIHIItem(e.target.value, type.type, item.name, 'Description');
                                                    }}
                                                />
                                            )}
                                            {dtoItem.Name === HISTORY_OF_RUNAWAY && dtoItem.Type?.Name === YesNoEnum.Yes && (
                                                <TextField
                                                    label="Details"
                                                    placeholder="Details"
                                                    fullWidth
                                                    variant="outlined"
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={Boolean(dtoItem?.Description) ? dtoItem?.Description : ''}
                                                    onChange={(e) => {
                                                        handleOnChangeSIHIItem(e.target.value, type.type, item.name, 'Description');
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    );
                                })}
                            </div>
                        );
                    })}
                </Stack>
            </Paper>
        </Grid>
    );
};

export default HomicidalIdeation;
